import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Eye,
  ExternalLink,
  Loader2,
  Settings,
  Sun,
  Moon,
  FileText,
  ListFilter,
  CalendarDays,
  Calendar,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { auth } from "../config/firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL;

export default function WidgetEventsPage() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [masjidInfo, setMasjidInfo] = useState(null);
  const [widgetToken, setWidgetToken] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  
  // Agenda Widget customization
  const [agendaTheme, setAgendaTheme] = useState("light");
  const [agendaColor, setAgendaColor] = useState("#bf9a2f");
  const [agendaWidth, setAgendaWidth] = useState("custom");
  const [agendaHeight, setAgendaHeight] = useState("custom");
  const [agendaCustomWidth, setAgendaCustomWidth] = useState(400);
  const [agendaCustomHeight, setAgendaCustomHeight] = useState(600);
  const [copiedAgenda, setCopiedAgenda] = useState(false);

  // Day Widget customization
  const [dayTheme, setDayTheme] = useState("light");
  const [dayColor, setDayColor] = useState("#bf9a2f");
  const [dayWidth, setDayWidth] = useState("custom");
  const [dayHeight, setDayHeight] = useState("custom");
  const [dayCustomWidth, setDayCustomWidth] = useState(500);
  const [dayCustomHeight, setDayCustomHeight] = useState(700);
  const [copiedDay, setCopiedDay] = useState(false);

  // Month Widget customization
  const [monthTheme, setMonthTheme] = useState("light");
  const [monthColor, setMonthColor] = useState("#bf9a2f");
  const [monthWidth, setMonthWidth] = useState("custom");
  const [monthHeight, setMonthHeight] = useState("custom");
  const [monthCustomWidth, setMonthCustomWidth] = useState(1200);
  const [monthCustomHeight, setMonthCustomHeight] = useState(800);
  const [copiedMonth, setCopiedMonth] = useState(false);

  // Scroll refs for navigation
  const agendaRef = useRef(null);
  const dayRef = useRef(null);
  const monthRef = useRef(null);

  useEffect(() => {
    const loadMasjidInfo = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/v1/masjid/admin/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setMasjidInfo(data);
        }
      } catch (err) {
        console.error("Error loading masjid info:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [user]);

  useEffect(() => {
    if (masjidInfo?.masjid?.id && !widgetToken && !generatingToken) {
      generateWidgetToken();
    }
  }, [masjidInfo]);

  const generateWidgetToken = async () => {
    try {
      setGeneratingToken(true);
      if (!auth.currentUser) return null;

      const token = await auth.currentUser.getIdToken(true);
      const masjidId = masjidInfo?.masjid?.id || userProfile?.masjidId || userProfile?.activeMasjidId;

      if (!masjidId) return null;

      const response = await fetch(`${API_BASE_URL}/v1/widget-token/${masjidId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.ok) {
        setWidgetToken(data.token);
        return data.token;
      }
    } catch (err) {
      console.error("Error generating widget token:", err);
    } finally {
      setGeneratingToken(false);
    }
    return null;
  };

  // Normalize dimension value
  const normalizeValue = (val, fallback, min = 200) => {
    if (val === "max") return "max";
    const num = Number(val);
    if (Number.isFinite(num) && num > 0) {
      return Math.max(min, num);
    }
    return fallback;
  };

  // ==================== AGENDA WIDGET HELPERS ====================
  const getAgendaWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = agendaColor.startsWith("#") ? agendaColor.substring(1) : agendaColor;
    const width = agendaWidth === "max" ? "max" : normalizeValue(agendaCustomWidth, 400);
    const height = agendaHeight === "max" ? "max" : normalizeValue(agendaCustomHeight, 600);
    return `${window.location.origin}/widget-event-agenda?token=${widgetToken}&theme=${agendaTheme}&color=${colorParam}&width=${width}&height=${height}`;
  };

  const getAgendaPreviewDimensions = () => {
    const width = agendaWidth === "max" ? "100%" : `${normalizeValue(agendaCustomWidth, 400)}px`;
    const height = agendaHeight === "max" ? "500px" : `${normalizeValue(agendaCustomHeight, 600)}px`;
    return { width, height };
  };

  const getAgendaEmbedCode = (widgetUrl) => {
    const widthStyle = agendaWidth === "max" ? "width: 100%;" : `width: ${normalizeValue(agendaCustomWidth, 400)}px; max-width: 100%;`;
    const heightStyle = agendaHeight === "max" ? "height: 100vh; min-height: 400px;" : `height: ${normalizeValue(agendaCustomHeight, 600)}px;`;
    return `<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: 1px solid #e5e7eb; border-radius: 12px; ${widthStyle} ${heightStyle} display: block; margin: 0 auto;"></iframe>`;
  };

  const copyAgendaCode = async () => {
    try {
      const widgetUrl = getAgendaWidgetUrl();
      if (!widgetUrl) return;

      if (agendaWidth !== "max") {
        const num = Number(agendaCustomWidth);
        if (!Number.isFinite(num) || num < 200) {
          window.alert("Please enter a valid width (200px or greater) before copying.");
          return;
        }
      }

      if (agendaHeight !== "max") {
        const num = Number(agendaCustomHeight);
        if (!Number.isFinite(num) || num < 200) {
          window.alert("Please enter a valid height (200px or greater) before copying.");
          return;
        }
      }

      const code = getAgendaEmbedCode(widgetUrl);
      await navigator.clipboard.writeText(code);
      setCopiedAgenda(true);
      setTimeout(() => setCopiedAgenda(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // ==================== DAY WIDGET HELPERS ====================
  const getDayWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = dayColor.startsWith("#") ? dayColor.substring(1) : dayColor;
    const width = dayWidth === "max" ? "max" : normalizeValue(dayCustomWidth, 500);
    const height = dayHeight === "max" ? "max" : normalizeValue(dayCustomHeight, 700);
    return `${window.location.origin}/widget-event-day?token=${widgetToken}&theme=${dayTheme}&color=${colorParam}&width=${width}&height=${height}`;
  };

  const getDayPreviewDimensions = () => {
    const width = dayWidth === "max" ? "100%" : `${normalizeValue(dayCustomWidth, 500)}px`;
    const height = dayHeight === "max" ? "550px" : `${normalizeValue(dayCustomHeight, 700)}px`;
    return { width, height };
  };

  const getDayEmbedCode = (widgetUrl) => {
    const widthStyle = dayWidth === "max" ? "width: 100%;" : `width: ${normalizeValue(dayCustomWidth, 500)}px; max-width: 100%;`;
    const heightStyle = dayHeight === "max" ? "height: 100vh; min-height: 500px;" : `height: ${normalizeValue(dayCustomHeight, 700)}px;`;
    return `<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: 1px solid #e5e7eb; border-radius: 12px; ${widthStyle} ${heightStyle} display: block; margin: 0 auto;"></iframe>`;
  };

  const copyDayCode = async () => {
    try {
      const widgetUrl = getDayWidgetUrl();
      if (!widgetUrl) return;

      if (dayWidth !== "max") {
        const num = Number(dayCustomWidth);
        if (!Number.isFinite(num) || num < 300) {
          window.alert("Please enter a valid width (300px or greater) before copying.");
          return;
        }
      }

      if (dayHeight !== "max") {
        const num = Number(dayCustomHeight);
        if (!Number.isFinite(num) || num < 400) {
          window.alert("Please enter a valid height (400px or greater) before copying.");
          return;
        }
      }

      const code = getDayEmbedCode(widgetUrl);
      await navigator.clipboard.writeText(code);
      setCopiedDay(true);
      setTimeout(() => setCopiedDay(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // ==================== MONTH WIDGET HELPERS ====================
  const getMonthWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = monthColor.startsWith("#") ? monthColor.substring(1) : monthColor;
    const width = monthWidth === "max" ? "max" : normalizeValue(monthCustomWidth, 1200);
    const height = monthHeight === "max" ? "max" : normalizeValue(monthCustomHeight, 800);
    return `${window.location.origin}/widget-event-month?token=${widgetToken}&theme=${monthTheme}&color=${colorParam}&width=${width}&height=${height}`;
  };

  const getMonthPreviewDimensions = () => {
    const width = monthWidth === "max" ? "100%" : `${normalizeValue(monthCustomWidth, 1200)}px`;
    const height = monthHeight === "max" ? "500px" : `${normalizeValue(monthCustomHeight, 800)}px`;
    return { width, height };
  };

  const getMonthEmbedCode = (widgetUrl) => {
    const widthStyle = monthWidth === "max" ? "width: 100%;" : `width: ${normalizeValue(monthCustomWidth, 1200)}px; max-width: 100%;`;
    const heightStyle = monthHeight === "max" ? "height: 100vh; min-height: 400px;" : `height: ${normalizeValue(monthCustomHeight, 800)}px;`;
    return `<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: 1px solid #e5e7eb; border-radius: 12px; ${widthStyle} ${heightStyle} display: block; margin: 0 auto;"></iframe>`;
  };

  const copyMonthCode = async () => {
    try {
      const widgetUrl = getMonthWidgetUrl();
      if (!widgetUrl) return;

      if (monthWidth !== "max") {
        const num = Number(monthCustomWidth);
        if (!Number.isFinite(num) || num < 300) {
          window.alert("Please enter a valid width (300px or greater) before copying.");
          return;
        }
      }

      if (monthHeight !== "max") {
        const num = Number(monthCustomHeight);
        if (!Number.isFinite(num) || num < 400) {
          window.alert("Please enter a valid height (400px or greater) before copying.");
          return;
        }
      }

      const code = getMonthEmbedCode(widgetUrl);
      await navigator.clipboard.writeText(code);
      setCopiedMonth(true);
      setTimeout(() => setCopiedMonth(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6">
          <Loader2 className="h-16 w-16 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Loading widget configuration...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/widgets")}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/10">
              <Calendar className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Event Widgets</h1>
              <p className="text-sm text-muted-foreground">
                Choose between Agenda, Day, or Month calendar views
              </p>
            </div>
          </div>
        </div>

        {/* Masjid Info Badge */}
        {masjidInfo?.masjid && (
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border bg-card px-4 py-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium">{masjidInfo.masjid.name}</span>
            </div>
          </div>
        )}

        {/* Quick Navigation */}
        <div className="flex flex-wrap justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => scrollToSection(agendaRef)}
            className="gap-2"
          >
            <ListFilter className="h-4 w-4" />
            Agenda View
          </Button>
          <Button
            variant="outline"
            onClick={() => scrollToSection(dayRef)}
            className="gap-2"
          >
            <CalendarDays className="h-4 w-4" />
            Day View
          </Button>
          <Button
            variant="outline"
            onClick={() => scrollToSection(monthRef)}
            className="gap-2"
          >
            <Calendar className="h-4 w-4" />
            Month View
          </Button>
        </div>

        {/* ==================== AGENDA VIEW WIDGET ==================== */}
        <div ref={agendaRef} className="scroll-mt-4">
          <div className="mb-6 flex items-center gap-3 border-b pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
              <ListFilter className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Agenda View</h2>
              <p className="text-sm text-muted-foreground">Scrollable list of upcoming events - great for sidebars</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Customization Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Customization</CardTitle>
                </div>
                <CardDescription>
                  Compact list format showing upcoming events chronologically
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={agendaTheme === "light" ? "default" : "outline"}
                      onClick={() => setAgendaTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={agendaTheme === "dark" ? "default" : "outline"}
                      onClick={() => setAgendaTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="agendaColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="agendaColor"
                      type="color"
                      value={agendaColor}
                      onChange={(e) => setAgendaColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={agendaColor}
                      onChange={(e) => setAgendaColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Dimensions</Label>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Use "Max" for full-width displays like TV screens or large monitors
                  </p>
                  
                  {/* Width */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Width</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={agendaWidth === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAgendaWidth("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={agendaWidth !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAgendaWidth("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {agendaWidth !== "max" && (
                      <Input
                        type="number"
                        min="200"
                        value={agendaCustomWidth}
                        onChange={(e) => setAgendaCustomWidth(e.target.value)}
                        className="text-sm"
                        placeholder="Width in pixels (min 200)"
                      />
                    )}
                  </div>

                  {/* Height */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={agendaHeight === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAgendaHeight("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={agendaHeight !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAgendaHeight("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {agendaHeight !== "max" && (
                      <Input
                        type="number"
                        min="200"
                        value={agendaCustomHeight}
                        onChange={(e) => setAgendaCustomHeight(e.target.value)}
                        className="text-sm"
                        placeholder="Height in pixels (min 200)"
                      />
                    )}
                  </div>
                </div>

                {/* Embed Code */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Embed Code</Label>
                    <Button
                      size="sm"
                      onClick={copyAgendaCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedAgenda ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="rounded-lg bg-muted/50 border p-4 max-h-32 overflow-y-auto">
                    {!widgetToken ? (
                      <div className="flex items-center justify-center py-4">
                        <p className="text-sm text-muted-foreground">
                          Generating token...
                        </p>
                      </div>
                    ) : (
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block">
                        {getAgendaEmbedCode(getAgendaWidgetUrl())}
                      </code>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Live Preview</CardTitle>
                  </div>
                  {widgetToken && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const widgetUrl = getAgendaWidgetUrl();
                        if (widgetUrl) {
                          window.open(widgetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </Button>
                  )}
                </div>
                <CardDescription>
                  See how your widget will look on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/30 overflow-hidden flex items-center justify-center p-4" style={{ minHeight: "500px" }}>
                  {widgetToken ? (
                    <iframe
                      src={getAgendaWidgetUrl()}
                      style={{ 
                        border: "none",
                        width: agendaWidth === "max" ? "100%" : `${Math.min(normalizeValue(agendaCustomWidth, 400), 450)}px`,
                        height: agendaHeight === "max" ? "500px" : `${Math.min(normalizeValue(agendaCustomHeight, 600), 500)}px`,
                        maxWidth: "100%",
                        display: "block"
                      }}
                      title="Agenda Widget Preview"
                    />
                  ) : (
                    <div className="flex h-full min-h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Preview: {agendaWidth === "max" ? "100%" : `${normalizeValue(agendaCustomWidth, 400)}px`} × {agendaHeight === "max" ? "Fill container" : `${normalizeValue(agendaCustomHeight, 600)}px`}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="mt-6 border-none bg-muted/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
                  <FileText className="h-5 w-5 text-orange-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">About this widget</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The Agenda View widget displays your upcoming events in a clean, scrollable list format. 
                    Perfect for embedding in sidebars or narrow spaces. Events are shown chronologically with 
                    date, time, title, and location. Clicking an event opens a detail modal with full description.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ==================== DAY VIEW WIDGET ==================== */}
        <div ref={dayRef} className="scroll-mt-4 pt-8">
          <div className="mb-6 flex items-center gap-3 border-b pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <CalendarDays className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Day View</h2>
              <p className="text-sm text-muted-foreground">Full-day timeline with event blocks and navigation</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Customization Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Customization</CardTitle>
                </div>
                <CardDescription>
                  Timeline view showing events scheduled throughout the day
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={dayTheme === "light" ? "default" : "outline"}
                      onClick={() => setDayTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={dayTheme === "dark" ? "default" : "outline"}
                      onClick={() => setDayTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="dayColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="dayColor"
                      type="color"
                      value={dayColor}
                      onChange={(e) => setDayColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={dayColor}
                      onChange={(e) => setDayColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Dimensions</Label>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Use "Max" for full-width displays like TV screens or large monitors
                  </p>
                  
                  {/* Width */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Width</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={dayWidth === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDayWidth("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={dayWidth !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDayWidth("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {dayWidth !== "max" && (
                      <Input
                        type="number"
                        min="300"
                        value={dayCustomWidth}
                        onChange={(e) => setDayCustomWidth(e.target.value)}
                        className="text-sm"
                        placeholder="Width in pixels (min 300)"
                      />
                    )}
                  </div>

                  {/* Height */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={dayHeight === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDayHeight("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={dayHeight !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setDayHeight("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {dayHeight !== "max" && (
                      <Input
                        type="number"
                        min="400"
                        value={dayCustomHeight}
                        onChange={(e) => setDayCustomHeight(e.target.value)}
                        className="text-sm"
                        placeholder="Height in pixels (min 400)"
                      />
                    )}
                  </div>
                </div>

                {/* Embed Code */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Embed Code</Label>
                    <Button
                      size="sm"
                      onClick={copyDayCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedDay ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="rounded-lg bg-muted/50 border p-4 max-h-32 overflow-y-auto">
                    {!widgetToken ? (
                      <div className="flex items-center justify-center py-4">
                        <p className="text-sm text-muted-foreground">
                          Generating token...
                        </p>
                      </div>
                    ) : (
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block">
                        {getDayEmbedCode(getDayWidgetUrl())}
                      </code>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Live Preview</CardTitle>
                  </div>
                  {widgetToken && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const widgetUrl = getDayWidgetUrl();
                        if (widgetUrl) {
                          window.open(widgetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </Button>
                  )}
                </div>
                <CardDescription>
                  See how your widget will look on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/30 overflow-hidden flex items-center justify-center p-4" style={{ minHeight: "500px" }}>
                  {widgetToken ? (
                    <iframe
                      src={getDayWidgetUrl()}
                      style={{ 
                        border: "none",
                        width: dayWidth === "max" ? "100%" : `${Math.min(normalizeValue(dayCustomWidth, 500), 500)}px`,
                        height: dayHeight === "max" ? "550px" : `${Math.min(normalizeValue(dayCustomHeight, 700), 550)}px`,
                        maxWidth: "100%",
                        display: "block"
                      }}
                      title="Day View Widget Preview"
                    />
                  ) : (
                    <div className="flex h-full min-h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Preview: {dayWidth === "max" ? "100%" : `${normalizeValue(dayCustomWidth, 500)}px`} × {dayHeight === "max" ? "Fill container" : `${normalizeValue(dayCustomHeight, 700)}px`}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="mt-6 border-none bg-muted/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">About this widget</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The Day View widget displays events in a timeline format, showing all events for a single day.
                    Users can navigate between days with previous/next buttons. Events are shown as blocks on the timeline
                    with their times visible. Great for showing daily schedules or when you have multiple events per day.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ==================== MONTH VIEW WIDGET ==================== */}
        <div ref={monthRef} className="scroll-mt-4 pt-8">
          <div className="mb-6 flex items-center gap-3 border-b pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
              <Calendar className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Month View</h2>
              <p className="text-sm text-muted-foreground">Traditional calendar grid with event dots - ideal for TV displays</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Preview Panel - Full Width on Top */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Live Preview</CardTitle>
                  </div>
                  {widgetToken && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const widgetUrl = getMonthWidgetUrl();
                        if (widgetUrl) {
                          window.open(widgetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Full Screen
                    </Button>
                  )}
                </div>
                <CardDescription>
                  See how your widget will look on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/30 overflow-hidden p-4" style={{ minHeight: "500px" }}>
                  {widgetToken ? (
                    <iframe
                      src={getMonthWidgetUrl()}
                      style={{ 
                        border: "none",
                        width: monthWidth === "max" ? "100%" : `${Math.min(normalizeValue(monthCustomWidth, 1200), 900)}px`,
                        height: monthHeight === "max" ? "500px" : `${Math.min(normalizeValue(monthCustomHeight, 800), 600)}px`,
                        maxWidth: "100%",
                        display: "block",
                        margin: "0 auto"
                      }}
                      title="Month View Widget Preview"
                    />
                  ) : (
                    <div className="flex h-full min-h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Preview: {monthWidth === "max" ? "100%" : `${normalizeValue(monthCustomWidth, 1200)}px`} × {monthHeight === "max" ? "Fill container" : `${normalizeValue(monthCustomHeight, 800)}px`}
                </div>
              </CardContent>
            </Card>

            {/* Customization Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Customization</CardTitle>
                </div>
                <CardDescription>
                  Classic calendar grid view with event indicators on each day
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={monthTheme === "light" ? "default" : "outline"}
                      onClick={() => setMonthTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={monthTheme === "dark" ? "default" : "outline"}
                      onClick={() => setMonthTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="monthColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="monthColor"
                      type="color"
                      value={monthColor}
                      onChange={(e) => setMonthColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={monthColor}
                      onChange={(e) => setMonthColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Dimensions</Label>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Use "Max" for full-width displays like TV screens or large monitors
                  </p>
                  
                  {/* Width */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Width</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={monthWidth === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthWidth("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={monthWidth !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthWidth("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {monthWidth !== "max" && (
                      <Input
                        type="number"
                        min="300"
                        value={monthCustomWidth}
                        onChange={(e) => setMonthCustomWidth(e.target.value)}
                        className="text-sm"
                        placeholder="Width in pixels (min 300)"
                      />
                    )}
                  </div>

                  {/* Height */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={monthHeight === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthHeight("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={monthHeight !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthHeight("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {monthHeight !== "max" && (
                      <Input
                        type="number"
                        min="400"
                        value={monthCustomHeight}
                        onChange={(e) => setMonthCustomHeight(e.target.value)}
                        className="text-sm"
                        placeholder="Height in pixels (min 400)"
                      />
                    )}
                  </div>
                </div>

                {/* Embed Code */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Embed Code</Label>
                    <Button
                      size="sm"
                      onClick={copyMonthCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedMonth ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="rounded-lg bg-muted/50 border p-4 max-h-32 overflow-y-auto">
                    {!widgetToken ? (
                      <div className="flex items-center justify-center py-4">
                        <p className="text-sm text-muted-foreground">
                          Generating token...
                        </p>
                      </div>
                    ) : (
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block">
                        {getMonthEmbedCode(getMonthWidgetUrl())}
                      </code>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="mt-6 border-none bg-muted/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                  <FileText className="h-5 w-5 text-purple-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">About this widget</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The Month View widget displays a traditional calendar grid showing the entire month.
                    Days with events are marked with colored dots. Clicking a day shows all events for that day.
                    Users can navigate between months. Ideal for giving visitors an overview of your event schedule.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
